import { useState } from 'react'
import type { Activity, EntityType, ActivityType } from '../../types'
import { ACTIVITY_LABELS } from '../../types'
import { useDataStore } from '../../stores/dataStore'
import { Button, Select, Textarea } from '../ui'
import { toast } from '../ui/Toast'

interface ActivityFeedProps {
  entityType: EntityType
  entityId: string
}

export default function ActivityFeed({ entityType, entityId }: ActivityFeedProps) {
  const { activities, addActivity, deleteActivity } = useDataStore()
  const [isAdding, setIsAdding] = useState(false)
  const [activityType, setActivityType] = useState<ActivityType>('note')
  const [content, setContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const entityActivities = activities
    .filter((a) => a.entityType === entityType && a.entityId === entityId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast('Content is required', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      await addActivity({
        entityType,
        entityId,
        type: activityType,
        content: content.trim(),
      })
      setContent('')
      setIsAdding(false)
      toast('Activity logged', 'success')
    } catch {
      toast('Failed to log activity', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteActivity(id)
      toast('Activity deleted', 'info')
    } catch {
      toast('Failed to delete', 'error')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const activityTypeOptions = Object.entries(ACTIVITY_LABELS).map(([value, label]) => ({
    value,
    label,
  }))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Activity</h3>
        {!isAdding && (
          <Button variant="ghost" size="sm" onClick={() => setIsAdding(true)}>
            + Log
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="bg-[#F5F5F3] rounded-lg p-3 space-y-3">
          <Select
            label="Type"
            options={activityTypeOptions}
            value={activityType}
            onChange={(e) => setActivityType(e.target.value as ActivityType)}
          />
          <Textarea
            label="Content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What happened?"
            rows={3}
          />
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAdding(false)
                setContent('')
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {entityActivities.length === 0 ? (
        <div className="empty-state py-6">
          <p className="empty-state-text">No activity yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entityActivities.map((activity) => (
            <ActivityItem
              key={activity.id}
              activity={activity}
              onDelete={() => handleDelete(activity.id)}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface ActivityItemProps {
  activity: Activity
  onDelete: () => void
  formatDate: (date: string) => string
}

function ActivityItem({ activity, onDelete, formatDate }: ActivityItemProps) {
  return (
    <div className="bg-[#F5F5F3] rounded-lg p-3 group">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-text-primary">
              {ACTIVITY_LABELS[activity.type]}
            </span>
            <span className="text-xs text-text-secondary">
              {formatDate(activity.createdAt)}
            </span>
          </div>
          <p className="text-sm text-text-primary whitespace-pre-wrap">
            {activity.content}
          </p>
        </div>
        <button
          onClick={onDelete}
          className="text-text-secondary hover:text-error opacity-0 group-hover:opacity-100 transition-opacity p-1"
          aria-label="Delete activity"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>
    </div>
  )
}
